"use client";

import { useState, useEffect } from "react";
import { signOut } from "next-auth/react";
import api from "@/lib/api";

interface WithdrawalModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
}

export default function WithdrawalModal({
  isOpen,
  onClose,
  userEmail,
}: WithdrawalModalProps) {
  const [emailId, setEmailId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Extract expected email ID (part before @naver.com)
  const expectedEmailId = userEmail.replace(/@naver\.com$/i, "").toLowerCase();
  const isConfirmValid = emailId.toLowerCase() === expectedEmailId;

  const handleWithdraw = async () => {
    if (!isConfirmValid) return;

    setError("");
    setLoading(true);

    try {
      await api.post("/api/user/withdraw");

      // Sign out and redirect to home
      await signOut({ callbackUrl: "/" });
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } } };
      setError(axiosError.response?.data?.error || "탈퇴 처리에 실패했습니다.");
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmailId("");
    setError("");
    onClose();
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-text/50" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-surface rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-text">회원 탈퇴</h2>
          <button
            onClick={handleClose}
            className="text-text-subtle hover:text-text-muted transition"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
              <svg
                className="w-8 h-8 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-text text-center mb-2">
              정말 탈퇴하시겠습니까?
            </h3>
            <p className="text-text-muted text-center text-sm">
              탈퇴 시 서비스 이용이 불가능하며,
              <br />
              아래 데이터에 접근할 수 없게 됩니다.
            </p>
          </div>

          <div className="bg-surface-muted rounded-lg p-4 mb-6">
            <ul className="space-y-2 text-sm text-text-muted">
              <li className="flex items-center gap-2">
                <span className="text-red-500">•</span>
                수강권 정보
              </li>
              <li className="flex items-center gap-2">
                <span className="text-red-500">•</span>
                출석 기록
              </li>
              <li className="flex items-center gap-2">
                <span className="text-red-500">•</span>
                예약 내역
              </li>
              <li className="flex items-center gap-2">
                <span className="text-red-500">•</span>
                프로필 정보
              </li>
            </ul>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-text mb-2">
              탈퇴를 원하시면 네이버 이메일을 입력해주세요.
            </label>
            <div className="flex">
              <input
                type="text"
                value={emailId}
                onChange={(e) => setEmailId(e.target.value)}
                placeholder="아이디"
                className="flex-1 px-3 py-2 border border-border-strong rounded-l-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-text placeholder:text-text-subtle"
              />
              <span className="inline-flex items-center px-3 py-2 border border-l-0 border-border-strong rounded-r-md bg-surface-muted text-text-subtle text-sm">
                @naver.com
              </span>
            </div>
          </div>

          {error && (
            <div className="text-red-500 text-sm text-center mb-4">{error}</div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 py-2 px-4 bg-surface-subtle text-text-muted rounded-md hover:bg-surface-subtle transition"
          >
            취소
          </button>
          <button
            onClick={handleWithdraw}
            disabled={!isConfirmValid || loading}
            className="flex-1 py-2 px-4 bg-red-500 text-text-inverse rounded-md hover:bg-red-600 transition disabled:bg-surface-subtle disabled:cursor-not-allowed"
          >
            {loading ? "처리 중..." : "탈퇴하기"}
          </button>
        </div>
      </div>
    </div>
  );
}
